/**
 * WeightedRandom — game-agnostic weighted selection utilities.
 *
 * Core primitive for any system that needs non-uniform random picks:
 * letter pools, loot tables, skill rarity, encounter generation, etc.
 *
 * Supports:
 *   - Single weighted pick (O(n) — fine for small-to-medium tables)
 *   - Pick N unique items without replacement, respecting weights
 *   - Weighted shuffle (order entire list by weighted priority)
 */

/** An item with a weight. Higher weight = more likely to be picked. */
export interface Weighted<T> {
  item: T;
  weight: number;
}

/**
 * Pick one item from a weighted list.
 * Weight 0 items are never picked.
 * Returns undefined if the list is empty or all weights are 0.
 */
export function weightedPick<T>(entries: ReadonlyArray<Weighted<T>>): T | undefined {
  let total = 0;
  for (const e of entries) total += e.weight;
  if (total <= 0) return undefined;

  let roll = Math.random() * total;
  for (const e of entries) {
    roll -= e.weight;
    if (roll <= 0) return e.item;
  }
  return entries[entries.length - 1]?.item;
}

/**
 * Pick N unique items without replacement, respecting weights.
 * If fewer than `count` items are available, returns all available.
 */
export function weightedPickN<T>(
  entries: ReadonlyArray<Weighted<T>>,
  count: number,
): T[] {
  // Copy so we can remove picked items
  const remaining = entries.filter(e => e.weight > 0).map(e => ({ ...e }));
  const result: T[] = [];

  while (result.length < count && remaining.length > 0) {
    let total = 0;
    for (const e of remaining) total += e.weight;
    if (total <= 0) break;

    let roll = Math.random() * total;
    for (let i = 0; i < remaining.length; i++) {
      roll -= remaining[i].weight;
      if (roll <= 0) {
        result.push(remaining[i].item);
        remaining.splice(i, 1);
        break;
      }
    }
  }

  return result;
}

/**
 * Weighted shuffle — sort the entire list by weighted random priority.
 * Higher weight items tend to appear earlier.
 * Uses the standard "sort by -weight * random^(1/weight)" trick.
 */
export function weightedShuffle<T>(entries: ReadonlyArray<Weighted<T>>): T[] {
  return entries
    .filter(e => e.weight > 0)
    .map(e => ({
      item: e.item,
      key: -Math.pow(Math.random(), 1 / e.weight),
    }))
    .sort((a, b) => a.key - b.key)
    .map(e => e.item);
}
